import { z } from 'zod';
/**
 * PART 9 — MANUFACTURING. The words, the shared shapes, and the one cost
 * breakdown every screen in the module reads.
 *
 * §9.4 is the reason this file exists rather than four independent ones:
 * "Every money figure in this module comes from ONE shared service, not from
 * the screen you are looking at… This matters because it is the single place
 * a developer can get the whole module wrong." The service's own comment in
 * the shipped code is quoted in the spec and is the rule the build follows:
 *
 *   "SHARED COSTING SERVICE — the single source of BOM cost math. Work Orders
 *    / cost rollups call this same function; do NOT inline cost math
 *    elsewhere."
 *
 * So `CostBreakdownSchema` is the ONE shape a cost is ever reported in, and
 * MFG-01's costing panel and MFG-02's planned cost are two readings of it.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
//
// Every one of these is quoted from PART 9. Where the spec gives a template
// ("Aluminum Housing is already on line 2") it is a function of the names it
// interpolates; where it gives a sentence outright it is a constant.
/**
 * D46 — the stage guard's two arguments travel as TOKENS, and these two maps
 * are the English half of what a token says. The raise sites used to pass the
 * verb phrase itself ('be edited'); a phrase cannot be an ICU select branch
 * (branch keys take no spaces), so the wire now carries `edit` and each
 * dictionary decides how to say it — en through the same phrases below (the
 * composed sentence is byte-identical to what always shipped), ar in Arabic.
 * Deliberately NOT exported: they are the twin's own composition detail, and
 * exporting a record of bare phrases would enrol it in the sentence census as
 * a carrier of sentences nobody raises.
 */
const WRONG_STAGE_ACTION_PHRASES = {
    edit: 'be edited',
    changeWarehouse: 'change warehouse',
    delete: 'be deleted',
    reserve: 'reserve',
    start: 'be started',
    close: 'be closed',
    hold: 'be held',
    resume: 'be resumed',
    substitute: 'be substituted on',
    reopenDone: 'be reopened from Done',
};
/**
 * Every value but one is a `WorkOrderStatusSchema` member and says itself —
 * the sentence has always interpolated the raw status. The odd one out is the
 * operation stepper's guard, which speaks about a 'completed operation'
 * rather than a work-order status; its token is `completedOperation` for the
 * same no-spaces reason as the actions.
 */
const WRONG_STAGE_STATUS_PHRASES = {
    draft: 'draft',
    scheduled: 'scheduled',
    inprogress: 'inprogress',
    completed: 'completed',
    closed: 'closed',
    onhold: 'onhold',
    cancelled: 'cancelled',
    completedOperation: 'completed operation',
};
export const MFG_MESSAGES = {
    // ── MFG-01 ───────────────────────────────────────────────────────────────
    /** BR-MFG-01-03 / ERR-MFG-01-01. */
    needsAComponent: 'Add at least one component before activating.',
    /** BR-MFG-01-04 / ERR-MFG-01-03. Names BOTH ends of the loop. */
    wouldLoop: (finished, through) => `${finished} cannot be a part of itself. ${through} already contains it.`,
    /** BR-MFG-01-05 / ERR-MFG-01-02. */
    duplicateComponent: (item, line) => `${item} is already on line ${line}.`,
    /** BR-MFG-01-06 / ERR-MFG-01-04, and ERR-MFG-02-02 on the work order. */
    qtyAboveZero: 'The quantity must be more than zero.',
    /** ERR-MFG-01-05. */
    scrapRange: 'Scrap must be between 0 and 100.',
    /** BR-MFG-01-07 / ERR-MFG-01-08. */
    versionInUse: (version, count) => `Version ${version} was used by ${count} work order${count === 1 ? '' : 's'}, so it cannot be deleted.`,
    /** BR-MFG-01-08. */
    effectiveDateBeforeIncumbent: (next, incumbent) => `Version ${next} starts before version ${incumbent} does. Pick a later date.`,
    /** BR-MFG-01-13 / ERR-MFG-01-07. */
    activeIsReadOnly: (version) => `Version ${version} is active. Create a new version to change the recipe.`,
    /** §8.5's activate prompt body — the real wording from the product's dictionary. */
    activatePromptTitle: 'Activate this version?',
    activatePromptBody: 'Another version is currently Active. Activating this one will deactivate the current active version.',
    /** BR-MFG-01-01 / ERR-MFG-01-06's own message, which names the two versions. */
    activateSwitchover: (incumbent, next) => `Version ${incumbent} is active now. Activating version ${next} will make version ${incumbent} inactive.`,
    /** BR-MFG-01-11 / ERR-MFG-01-09 — a WARNING, never a block. */
    lonelyWorkstation: (workstation) => `No other operation uses '${workstation}'. Check the spelling.`,
    /** §15's two-users-at-once row: the first commit wins. */
    activatedElsewhere: (version) => `Version ${version} was activated a moment ago.`,
    /** §8.4's empty state. */
    notUsedAnywhere: 'Not used in any other BOM.',
    /** A recipe is written against a stocked PRODUCT. See the note in bom.ts. */
    finishedItemMustBeProduct: (item) => `${item} is not something the workshop makes. A recipe needs a stocked product.`,
    // ── MFG-02 ───────────────────────────────────────────────────────────────
    /**
     * BR-MFG-01-15 / BR-MFG-02-01 / ERR-MFG-02-01.
     *
     * THE SPEC QUOTES TWO WORDINGS OF THIS ONE REFUSAL and this is the one
     * built. ERR-MD-03-08 words it from the item screen ("… has no bill of
     * materials. Add one before making it."), but this build has no Made item
     * type — items are product / service / bundle — so the item editor never
     * knows an item is manufactured. The first seam where the question exists
     * is the work order that tries to make it, and that seam speaks
     * BR-MFG-02-01's sentence. One sentence, at the only seam there is.
     */
    noActiveBom: (item) => `${item} has no active bill of materials. Create one first.`,
    /** BR-MFG-02-11 / ERR-MFG-02-03. */
    belowProduced: (produced) => `${produced} have already been made. The target cannot go below ${produced}.`,
    /** ERR-MFG-02-04. */
    endBeforeStart: 'The end date must be on or after the start date.',
    /** BR-MFG-02-05 / ERR-MFG-02-05 — a note, not a refusal: it reserves what it can. */
    cappedByOnHand: (onHand, item) => `Only ${onHand} ${item} are on hand. ${onHand} reserved.`,
    /** §8.5's shortage modal / ERR-MFG-02-06 — the real wording from the product's dictionary. */
    shortageTitle: 'Material shortage',
    shortageBody: 'One or more components have insufficient stock for the required quantity. Start production anyway?',
    /** §8.1's passive banner. It never names the short component — the row chip does. */
    shortageBanner: 'Some components are short on stock',
    /** BR-MFG-02-07 / BR-MFG-02-23: the check has no bypass, whichever control triggered it. */
    shortageNotAcknowledged: 'One or more components are short. Confirm you want to start anyway before this job begins.',
    /** BR-MFG-02-08 / ERR-MFG-02-07. */
    substitutionNeedsReason: 'Say why you are swapping this part.',
    /** BR-MFG-02-13 / ERR-MFG-02-09. */
    alreadyUsedParts: 'Parts have already been used on this job. Complete it instead.',
    /** ERR-MFG-02-10. */
    jobFinished: 'This job is finished. Nothing more can be logged.',
    /** §15's Zero row. */
    logSomething: 'Type how many you made.',
    /** BR-MFG-02-24 — the sentence that replaces the ruled-out one-click completion. */
    confirmWhatWasMade: 'Check what was made before finishing this job.',
    /** §15's two-users-at-once row. */
    completedElsewhere: 'This job was completed a moment ago.',
    /**
     * The stage guard, in the spec's own voice (§7's "What you CANNOT do").
     * D46: both arguments are TOKENS (see the phrase maps above the record);
     * the composed sentence is unchanged, byte for byte, for every pair the
     * raise sites pass.
     */
    wrongStage: (action, status) => `A ${WRONG_STAGE_STATUS_PHRASES[status]} work order cannot ${WRONG_STAGE_ACTION_PHRASES[action]}.`,
    /** BR-MFG-03-15 / ERR-MFG-03-06. */
    startedCannotMove: (code) => `${code} has already started and cannot be moved.`,
    // ── MFG-03 ───────────────────────────────────────────────────────────────
    /** BR-MFG-03-02 / ERR-MFG-03-01. */
    forecastFinishedOnly: 'You can only forecast finished products.',
    /** ERR-MFG-03-02. */
    periodShape: 'Type the month as YYYY-MM, for example 2026-08.',
    /** BR-MFG-03-10 / ERR-MFG-03-04. */
    alreadyConverted: (code) => `This plan is already work order ${code}.`,
    /** BR-MFG-03-18 / ERR-MFG-03-05. */
    cannotBePlanned: (item) => `${item} has no active bill of materials, so it cannot be planned.`,
    /** BR-MFG-03-06 / ERR-MFG-03-07 — shown on the row, in red. */
    lateOrder: (on) => `Late — this should have been ordered on ${on}.`,
    /** §15's two-planners row. */
    planRunning: 'The plan is being recalculated. This will take a moment.',
    /** §15's Empty row. */
    nothingWaiting: 'Nothing is waiting to be made.',
    /** §5.7's happy state. */
    noShortfalls: 'No shortfalls — stock and incoming POs cover all demand.',
    // ── MFG-04 ───────────────────────────────────────────────────────────────
    /** BR-MFG-04-23's refusal, in the spec's own words. */
    correctionNotAllowed: 'You cannot correct a posted voucher. Ask an accountant.',
    /** BR-MFG-04-01 / ERR-MFG-04-01. THE sentence of the screen. */
    postedImmutable: 'Posted transactions are immutable. Create a correction instead.',
    /** §5.3's immutability strip, rendered exactly. */
    immutabilityStrip: 'Posted transactions are immutable — corrections create a new linked entry.',
    /** §5.5's correction banner, rendered exactly. */
    correctionBanner: 'This posts a new Return-to-Stock entry linked to the original — history is never edited.',
    /** BR-MFG-04-04 / ERR-MFG-04-02. */
    needsALine: 'Add at least one item and quantity.',
    /** BR-MFG-04-03 / BR-MFG-04-20 / ERR-MFG-04-03 / ERR-MFG-04-04. One sentence, both cases. */
    sayWhy: 'Say why this needs correcting.',
    /** BR-MFG-04-09 / ERR-MFG-04-07. */
    notYet: 'That time has not happened yet.',
    /** ERR-MFG-04-08. */
    dateTimeShape: 'Type the date and time as YYYY-MM-DD HH:MM.',
    /** BR-MFG-04-16 / BR-MFG-04-21, the half a user can trip. */
    indirectHasNoJob: 'An indirect issue cannot be linked to a work order.',
    /** BR-MFG-04-21's other half. */
    jobNeedsAType: 'A voucher with no work order must be an Indirect issue.',
    /** §5.6's hint line, worded exactly. */
    varianceHint: 'Planned from BOM snapshot; actual is the net of consumption ledger rows (incl. returns). Click a % to drill down.',
    /** §5.7's footer line, worded exactly. */
    usageFeedsMrp: 'Structured to feed Production Planning MRP as historical usage.',
    /** §8's empty ledger. */
    noMatchingRows: 'No transactions match these filters.',
};
/**
 * D46 — PART 9's structured refusals: key + params beside the composed
 * English sentence, so a refusal that names a version, an item, a code or a
 * count reaches an Arabic reader in Arabic. One builder per parameterised
 * member the API raises inside an HTTP exception body — the raise sites throw
 * THESE, and each `message` is composed by the `MFG_MESSAGES` twin so the two
 * cannot drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * Three parameterised MFG sentences are deliberately NOT here:
 * `lonelyWorkstation` and `lateOrder` are composed client-side (the API never
 * raises them), and `cappedByOnHand` is a note riding a 2xx response — the
 * census records it under NOT_REFUSAL_BODIES.
 *
 * `wrongStage`'s two params are TOKENS, not prose: the wire carries `edit` /
 * `inprogress` and each dictionary decides how to say them (en composes the
 * same phrases the sentence always used; ar selects). Params stay
 * locale-neutral, which is what lets one body serve both readers.
 */
export const MFG_REFUSALS = {
    wouldLoop: (finished, through) => ({
        message: MFG_MESSAGES.wouldLoop(finished, through),
        key: 'mfg.errWouldLoop',
        params: { finished, through },
    }),
    duplicateComponent: (item, line) => ({
        message: MFG_MESSAGES.duplicateComponent(item, line),
        key: 'mfg.errDuplicateComponent',
        params: { item, line },
    }),
    versionInUse: (version, count) => ({
        message: MFG_MESSAGES.versionInUse(version, count),
        key: 'mfg.errVersionInUse',
        params: { version, count },
    }),
    effectiveDateBeforeIncumbent: (next, incumbent) => ({
        message: MFG_MESSAGES.effectiveDateBeforeIncumbent(next, incumbent),
        key: 'mfg.errEffectiveDateBeforeIncumbent',
        params: { next, incumbent },
    }),
    activeIsReadOnly: (version) => ({
        message: MFG_MESSAGES.activeIsReadOnly(version),
        key: 'mfg.errActiveIsReadOnly',
        params: { version },
    }),
    activateSwitchover: (incumbent, next) => ({
        message: MFG_MESSAGES.activateSwitchover(incumbent, next),
        key: 'mfg.errActivateSwitchover',
        params: { incumbent, next },
    }),
    activatedElsewhere: (version) => ({
        message: MFG_MESSAGES.activatedElsewhere(version),
        key: 'mfg.errActivatedElsewhere',
        params: { version },
    }),
    finishedItemMustBeProduct: (item) => ({
        message: MFG_MESSAGES.finishedItemMustBeProduct(item),
        key: 'mfg.errFinishedItemMustBeProduct',
        params: { item },
    }),
    noActiveBom: (item) => ({
        message: MFG_MESSAGES.noActiveBom(item),
        key: 'mfg.errNoActiveBom',
        params: { item },
    }),
    belowProduced: (produced) => ({
        message: MFG_MESSAGES.belowProduced(produced),
        key: 'mfg.errBelowProduced',
        params: { produced },
    }),
    wrongStage: (action, status) => ({
        message: MFG_MESSAGES.wrongStage(action, status),
        key: 'mfg.errWrongStage',
        params: { action, status },
    }),
    startedCannotMove: (code) => ({
        message: MFG_MESSAGES.startedCannotMove(code),
        key: 'mfg.errStartedCannotMove',
        params: { code },
    }),
    alreadyConverted: (code) => ({
        message: MFG_MESSAGES.alreadyConverted(code),
        key: 'mfg.errAlreadyConverted',
        params: { code },
    }),
};
// ── Shared shapes ───────────────────────────────────────────────────────────
/**
 * §9.4's ONE formula, reported once. Every figure is a decimal STRING (§2.1).
 *
 * THE ROUNDING RULE, and it is the one thing §9.4 warns the build about
 * twice: the service adds the component lines UNROUNDED and rounds ONCE, at
 * the end. The six lines of the shipped Desk Lamp recipe each rounded to 2 dp
 * add to 16.24; the material total is 16.25, and "the tile is right and the
 * column does not lie — they are answering slightly different questions. Do
 * not 'fix' this by rounding each line before summing; that would make the
 * work order's planned cost disagree with the recipe's."
 *
 * (§15's "Rounding leftovers" bullet says the opposite — that each line is
 * rounded before adding. §9.4 states the rule twice, explains the consequence,
 * and §16's definition of done pins the answer to 16.25, so §9.4 wins and the
 * edge-case bullet is the contradicted one. `materialCost` is therefore NOT
 * always the sum of the displayed `lineCost` values, by design.)
 */
export const CostLineSchema = z
    .object({
    itemId: z.uuid(),
    /** Field 12: `qty × (1 + scrap ÷ 100)`, 3 dp. The quantity a job requires. */
    qtyWithScrap: z.string(),
    /** Field 13: from the item's average cost — never typed (BR-MFG-01-12). */
    unitCost: z.string(),
    /** Field 14: `qty +scrap × unit cost`, rounded to 2 dp for display only. */
    lineCost: z.string(),
})
    .strict();
export const CostBreakdownSchema = z
    .object({
    /** Field 21: the sum of every line, added UNROUNDED and rounded once. */
    materialCost: z.string(),
    /** Field 22: `total routing minutes × labor rate`. */
    laborCost: z.string(),
    /** Field 23: `@ 0.75/min` — one company-wide rate, no per-workstation rate. */
    laborRatePerMinute: z.string(),
    totalMinutes: z.number().int(),
    /** Field 24: the only typed figure. */
    overheadPct: z.string(),
    /** Field 25: `overhead % × (material + labor)` — NOT of material alone. */
    overhead: z.string(),
    /** Field 26: `material + labor + overhead`. */
    totalPerUnit: z.string(),
    lines: CostLineSchema.array(),
})
    .strict();
/** §7 — three statuses per VERSION, not per item (OQ-9.7-2). */
export const BomStatusSchema = z.enum(['draft', 'active', 'inactive']);
/** MFG-02 §7 — five linear stages plus two off-stepper exceptions. */
export const WorkOrderStatusSchema = z.enum([
    'draft',
    'scheduled',
    'inprogress',
    'completed',
    'closed',
    'onhold',
    'cancelled',
]);
export const WorkOrderPrioritySchema = z.enum(['low', 'normal', 'urgent']);
/** Field 22 — one click-to-cycle button; §5.4's decision: it must NOT wrap. */
export const OperationStatusSchema = z.enum(['not_started', 'in_progress', 'done']);
/**
 * MFG-04 §5.5, and the names are the product's own (OQ-9.7-10). Exactly four.
 * There is no scrap type: scrapped OUTPUT is a work-order quantity, wasted
 * INPUT is an Extra issue.
 */
export const IssueTypeSchema = z.enum(['planned', 'extra', 'return_to_stock', 'indirect']);
/** MFG-03 §5.2 — the three kinds of demand, colour-coded per row. */
export const DemandSourceSchema = z.enum(['sales_order', 'forecast', 'reorder_point']);
/**
 * A percentage 0..100 with up to FOUR decimals — scrap and overhead, which
 * PART 9 shows to 2 dp on screen but which multiply quantities rather than
 * money, so the stored precision is wider than `PctStringSchema`'s.
 */
export const MfgPctSchema = z
    .string()
    .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Percentages are decimal strings')
    .refine((value) => Number.parseFloat(value) <= 100, MFG_MESSAGES.scrapRange);
/** Field 11 on MFG-03: a MONTH, typed by hand as YYYY-MM (never a date picker). */
export const ForecastPeriodSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, MFG_MESSAGES.periodShape);
/*
 * WO-YYYY-#### and MC-#### used to be formatted here, as two pure functions
 * over the number. They are not here any more, and the reason is MD-13: the
 * prefix and the width are a TENANT'S SETTING, read from `number_sequences`,
 * so a code cannot be produced without a transaction to read it in. Both moved
 * to `apps/api/src/platform/number-format.ts` (`numberFormat(tx, 'work_order',
 * year)` and `numberFormat(tx, 'consumption_voucher')`), which is also where
 * GEN-77a's year-in-the-code lives now.
 *
 * `formatBomVersion` stays, and the difference is the point: `v3` is not a
 * §4.9 document number at all — it is a version chip inside one recipe, and
 * MD-13 does not offer a tenant a prefix for it.
 */
/** Field 4: the `v3` chip. Pattern `v#`, counting from v1. */
export function formatBomVersion(no) {
    return `v${no}`;
}
//# sourceMappingURL=manufacturing.js.map